import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import { DeleteOutlined, MoreVertOutlined } from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import { type FC, type MouseEvent, useRef, useState } from "react"

import { ConfirmationDialog } from "@/components/common/Modals"
import type { SignedUrl } from "@/graphql/codegen/graphql"

interface PurchaseOrderFileRowActionsProps {
  row: CellContext<SignedUrl, unknown>["row"]
  onRemove: (fileId: string) => Promise<void>
}

export const PurchaseOrderFileRowActions: FC<PurchaseOrderFileRowActionsProps> = ({ row, onRemove }) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [openedDialog, setOpenedDialog] = useState(false)

  const handleToggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  const handleStopPropagation = (event: MouseEvent<HTMLElement>) => event.stopPropagation()

  const handleRemoveDocument = async () => {
    await onRemove(row.original?.file?.fileId as string)

    setOpenedDialog(false)
  }

  return (
    <Box className='flex items-center justify-center' ref={anchorElRef} onClick={handleStopPropagation}>
      <IconButton onClick={handleToggleDropdown} data-testid='purchase-order-file-row-actions-button'>
        <MoreVertOutlined />
      </IconButton>

      <Menu
        anchorEl={anchorElRef.current}
        open={isDropdownOpen}
        onClose={handleToggleDropdown}
        data-test='purchase-order-file-row-dropdown-menu'
      >
        <MenuItem onClick={() => setOpenedDialog(true)} data-test='purchase-order-file-update-status-menu-item'>
          <ListItemIcon>
            <DeleteOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
          </ListItemIcon>
          <ListItemText primary='Delete Document' />
        </MenuItem>
      </Menu>
      {openedDialog && (
        <ConfirmationDialog
          title='Are you sure you want to remove this file?'
          content='This action will delete the Purchase Order file'
          closeLabel='Cancel'
          confirmLabel='Yes, Remove'
          onClose={() => setOpenedDialog(false)}
          onConfirm={handleRemoveDocument}
        />
      )}
    </Box>
  )
}
